/**
 * PrintingInfo container component
 * Orchestrates PrintingImagesCarousel and PrintingGraphs with shared state
 *
 * Features:
 * - Fetches print-photo events once (eliminates duplicate API calls)
 * - Parses reportUrl CSV data once at container level
 * - Manages shared activeEventIndex state for sync between carousel and graphs
 * - Passes events, reportData, activeEventIndex as props to children
 * - Handles loading/error states at container level
 */

import { useState, useEffect } from 'react';
import { useRecordContext, useDataProvider, Loading } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { Printing, Event } from '../../types';
import { PrintingImagesCarousel } from './PrintingImagesCarousel';
import { PrintingGraphs } from './PrintingGraphs';

interface ReportRow {
  index: number;
  cncE: string;
  gcodePosition: string;
  feederP: string;
}

interface ReportData {
  rows: ReportRow[];
  min: number;
  max: number;
}

export const PrintingInfo = () => {
  const record = useRecordContext<Printing>();
  const dataProvider = useDataProvider();
  const [events, setEvents] = useState<Event[]>([]);
  const [reportData, setReportData] = useState<ReportData | undefined>(undefined);
  const [activeEventIndex, setActiveEventIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!record?.id) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        // First, get the print-photo event type
        const eventTypeRes = await dataProvider.getList('eventTypes', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' },
          filter: { name: 'print-photo' },
        });

        if (!eventTypeRes.data.length) {
          setError('No print-photo event type found');
          setLoading(false);
          return;
        }

        // Fetch all print-photo events for this printing
        const eventsRes = await dataProvider.getList('events', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'eventTime', order: 'ASC' },
          filter: {
            eventTypeId: eventTypeRes.data[0].id,
            printingId: record.id,
          },
        });

        const allEvents = eventsRes.data as Event[];
        setEvents(allEvents);
        // Set initial activeEventIndex to the last event (most recent)
        setActiveEventIndex(allEvents.length - 1);

        // If reportUrl exists, fetch and parse the CSV
        if (record.reportUrl) {
          try {
            const response = await fetch(record.reportUrl);
            const csvText = await response.text();
            const lines = csvText.split('\n');
            const keysLine = lines.splice(0, 1)[0];
            const keys = keysLine.split(';');
            const rows: ReportRow[] = lines
              .map((line, index) => {
                const element: Partial<ReportRow> = { index };
                const lineParts = line.split(';');
                for (let i = 0, il = keys.length; i < il; i++) {
                  const key = keys[i];
                  (element as Record<string, string>)[key] = lineParts[i];
                }
                return element as ReportRow;
              })
              .filter((row) => row.cncE && row.gcodePosition && row.feederP);

            let min = 60000;
            let max = -1000;
            rows.forEach((row) => {
              const { cncE, gcodePosition, feederP } = row;
              if (min > parseFloat(cncE)) min = parseFloat(cncE);
              if (max < parseFloat(cncE)) max = parseFloat(cncE);

              if (min > parseFloat(gcodePosition)) min = parseFloat(gcodePosition);
              if (max < parseFloat(gcodePosition)) max = parseFloat(gcodePosition);

              if (min > parseFloat(feederP)) min = parseFloat(feederP);
              if (max < parseFloat(feederP)) max = parseFloat(feederP);
            });

            setReportData({ rows, min, max });
          } catch {
            // Silently fail if report fetch fails - component will work without it
          }
        }

        setLoading(false);
      } catch {
        setError('Failed to load printing data');
        setLoading(false);
      }
    };

    fetchData();
  }, [record?.id, record?.reportUrl, dataProvider]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!events.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No visualization data available for this printing.</Typography>
      </Box>
    );
  }

  // Filter events with photos for the carousel
  const eventsWithPhotos = events.filter((event) => event.photoUrl);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {events.length} event{events.length !== 1 ? 's' : ''} available
        {eventsWithPhotos.length > 0 &&
          ` (${eventsWithPhotos.length} photo${eventsWithPhotos.length !== 1 ? 's' : ''})`}
      </Typography>

      {/* Photo Carousel Section */}
      {eventsWithPhotos.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Photos
          </Typography>
          <PrintingImagesCarousel
            data={eventsWithPhotos}
            index={activeEventIndex}
            onSlideChange={(index) => setActiveEventIndex(index)}
          />
        </Box>
      )}

      {/* Graphs Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Graphs
        </Typography>
        <PrintingGraphs
          events={events}
          reportdata={reportData}
          activeevent={activeEventIndex}
          onActiveIndexChanged={setActiveEventIndex}
        />
      </Box>
    </Box>
  );
};

export default PrintingInfo;
